import { ButtonProps, TextFieldProps } from "@mui/material";
import {
  GridCellParams,
  GridColumns,
  GridDensity,
  MuiEvent,
} from "@mui/x-data-grid-premium";
import { greyStyleForNonEditableCells } from "./helpers/dataGridHelpers";

export const defaultTextFieldProps: TextFieldProps = {
  size: "small",
  variant: "filled",
  autoComplete: "off",
  fullWidth: true,
};

export const injectDefaultColumnProps = (
  p: GridColumns<any>
): GridColumns<any> => {
  // for (const key in p) {
  //   if (Object.prototype.hasOwnProperty.call(p, key)) {
  //     const element = p[key];
  //     element.headerClassName = "header";
  //   }
  // }

  return p;
};

export const handleArrowNavigationForNumberCells = (
  params: GridCellParams,
  event: MuiEvent<any>
) => {
  const key = event.key;
  if (key !== "ArrowUp" && key !== "ArrowDown") return;

  const target = event.target as HTMLElement | null;
  const isNumberInput =
    (target instanceof HTMLInputElement && target.type === "number") ||
    target?.getAttribute("role") === "spinbutton";

  if (!isNumberInput) return;

  event.preventDefault();
  event.stopPropagation();

  const api = (params as any).api;
  const rowIds = api?.getAllRowIds?.() ?? [];
  const currentIndex = rowIds.findIndex((rowId: any) => rowId === params.id);
  if (currentIndex < 0) return;

  const nextIndex =
    key === "ArrowDown"
      ? Math.min(currentIndex + 1, rowIds.length - 1)
      : Math.max(currentIndex - 1, 0);
  const nextRowId = rowIds[nextIndex];
  if (nextRowId === undefined || nextRowId === params.id) return;

  api.stopCellEditMode?.({ id: params.id, field: params.field });
  api.scrollToIndexes?.({ rowIndex: nextIndex });
  api.setCellFocus?.(nextRowId, params.field);
};

export const defaultDataGridProps: {
  density: GridDensity;
  autoHeight: boolean;
  rowSelection: boolean;
  cellSelection: boolean;
  disableClipboardPaste: boolean;
  clipboardCopyCellDelimiter: string;
  disableRowSelectionOnClick: boolean;
  disableColumnFilter: boolean;
  disableColumnMenu: boolean;
  disableChildrenSorting: boolean;
  disableDensitySelector: boolean;
  disableColumnSelector: boolean;
  disableColumnReorder: boolean;
  onProcessRowUpdateError: (error: unknown) => void;
  onCellKeyDown: (params: GridCellParams, event: MuiEvent<any>) => void;
  getCellClassName: (params: GridCellParams<number, any, number>) => string;
  sx: {
    flex: number;
    minWidth: number;
    minHeight: number;
    width: string;
    height: string;
    overflow: string;
    "& .MuiDataGrid-cell": {
      display: string;
      alignItems: string;
    };
    "& .MuiDataGrid-cellContent": {
      display: string;
      alignItems: string;
      width: string;
      height: string;
    };
    "& .MuiDataGrid-main": {
      minHeight: number;
    };
    "& .MuiDataGrid-virtualScroller": {
      overflowY: string;
    };
  };
} = {
  density: "compact",
  autoHeight: false,
  rowSelection: false,
  cellSelection: true,
  disableClipboardPaste: false,
  clipboardCopyCellDelimiter: "\t",
  disableColumnFilter: true,
  disableColumnMenu: true,
  disableRowSelectionOnClick: true,
  disableChildrenSorting: true,
  disableColumnSelector: true,
  disableDensitySelector: true,
  disableColumnReorder: true,
  onProcessRowUpdateError: (error: unknown) => {
    console.error("DataGrid processRowUpdate failed", error);
  },
  sx: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    "& .MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
    },
    "& .MuiDataGrid-cellContent": {
      display: "flex",
      alignItems: "center",
      width: "100%",
      height: "100%",
    },
    "& .MuiDataGrid-main": {
      minHeight: 0,
    },
    "& .MuiDataGrid-virtualScroller": {
      overflowY: "auto",
    },
  },
  onCellKeyDown: handleArrowNavigationForNumberCells,
  getCellClassName: greyStyleForNonEditableCells,
};

export const defaultButtonProps: ButtonProps = {
  size: "small",
  variant: "contained",
  fullWidth: true,
  sx: { color: "white" },
};

export const ratesTabID = "ratesTab";
export const projectTab = "projectTab";
export const talentTab = "talentTab";

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

export const formatCurrency = (v: number): string => {
  let p = Math.round(v * 100) / 100;
  if (p === -0) p = 0; //eslint-disable-line
  const ret = currencyFormatter.format(p);
  return ret;
};

export const font = "dharma-gothic-e, sans-serif;";
